import { IconButton, InputAdornment, Switch, TextField, Tooltip } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";

function ToggleTexto({
	ativo,
	rotulo,
	onClick,
}: {
	readonly ativo: boolean;
	readonly rotulo: string;
	readonly onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex h-7.5 select-none items-center gap-1 rounded-lg border bg-paper pl-1 pr-3 text-sm text-foreground transition-colors hover:bg-muted/60"
		>
			<Switch checked={ativo} size="small" />
			{rotulo}
		</button>
	);
}

export type ToolbarRestricoesRecursoProps = {
	readonly grupoIdLabel: string;
	readonly grupoNome: string;
	readonly totalPermissoes: number;
	readonly permissoesAtivas: number;
	readonly buscaPermissao: string;
	readonly somenteAtivas: boolean;
	readonly temAlteracoes: boolean;
	readonly qtdAlteracoes: number;
	readonly onBuscarPermissao: (valor: string) => void;
	readonly onAlternarSomenteAtivas: () => void;
	readonly onAbrirGrupos?: () => void;
	readonly onAuditoria?: () => void;
};

export default function ToolbarRestricoesRecurso({
	grupoIdLabel,
	grupoNome,
	totalPermissoes,
	permissoesAtivas,
	buscaPermissao,
	somenteAtivas,
	temAlteracoes,
	qtdAlteracoes,
	onBuscarPermissao,
	onAlternarSomenteAtivas,
	onAbrirGrupos,
	onAuditoria,
}: ToolbarRestricoesRecursoProps) {
	const rotuloAlteracoes = qtdAlteracoes === 1 ? "1 alteração não salva" : `${qtdAlteracoes} alterações não salvas`;

	return (
		<div className="flex shrink-0 flex-col gap-4 border-b bg-paper px-4 py-4">
			<div className="flex flex-wrap items-end gap-4">
				<div className="flex min-w-0 flex-1 items-center gap-3">
					{onAbrirGrupos ? (
						<IconButton
							size="small"
							className="shrink-0 md:hidden"
							aria-label="Abrir grupos"
							onClick={onAbrirGrupos}
						>
							<FuseSvgIcon size={20}>lucide:menu</FuseSvgIcon>
						</IconButton>
					) : null}
					<span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-lg font-extrabold text-primary-foreground">
						{grupoIdLabel}
					</span>
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-lg font-semibold text-foreground">{grupoNome}</span>
						<span className="text-sm font-semibold text-muted-foreground">
							{permissoesAtivas} de {totalPermissoes} permissões ativas
						</span>
					</div>

					<Tooltip title="Auditoria" arrow placement="top">
						<span className="shrink-0">
							<IconButton
								size="small"
								aria-label="Auditoria do recurso"
								className="text-muted-foreground hover:text-primary"
								disabled={!onAuditoria}
								onClick={onAuditoria}
							>
								<FuseSvgIcon size={20}>lucide:scroll-text</FuseSvgIcon>
							</IconButton>
						</span>
					</Tooltip>
				</div>

				{temAlteracoes ? (
					<span className="flex items-center gap-2 text-xs font-semibold text-warning">
						<span className="h-2 w-2 rounded-full bg-warning" />
						{rotuloAlteracoes}
					</span>
				) : null}
			</div>

			<div className="flex flex-wrap items-center gap-3">
				<TextField
					value={buscaPermissao}
					onChange={(evento) => onBuscarPermissao(evento.target.value)}
					placeholder="Buscar permissão…"
					size="small"
					className="w-full sm:flex-1 md:w-1/3"
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<FuseSvgIcon size={18}>lucide:search</FuseSvgIcon>
								</InputAdornment>
							),
						},
					}}
				/>

				<ToggleTexto ativo={somenteAtivas} rotulo="Somente ativas" onClick={onAlternarSomenteAtivas} />
			</div>
		</div>
	);
}
